import invariant from 'invariant';
import times from 'lodash/times';
import { ASTExpr } from '../lscript/ast';
import { evalLimitedExpression } from '../lscript/eval';
import { Context } from '../lscript/types';
import must from '../utils/must';
import { AmbiguousConversionError } from './AmbiguousConversionError';
import { MultipleBaseUnitsError } from './MultipleBaseUnitsError';
import { NoConversionError } from './NoConversionError';
import {
  expandUnit,
  exponentiateUnit,
  isUnitEqual,
  multiplyUnits,
} from './operations';
import {
  baseUnitsFor,
  findBaseUnits,
  getUnitBang,
  isBaseUnit,
  NullUnit,
  registryGetConversion,
} from './registry';
import { type Unit } from './types';
import { emptyContext } from '../lscript/utils';

/*
 * Find or make a converter from one unit to another. If a common unit is not
 * provided, the converter will be made from the shared base unit.
 */
export function findUnitConverterAst(
  from: Unit,
  to: Unit,
  common?: Unit
): (v: ASTExpr) => ASTExpr {
  if (isUnitEqual(from, to)) {
    return (v: ASTExpr) => v;
  }

  // Look for a one-step conversion
  const fromTo = registryGetConversion(from, to);
  if (fromTo) {
    return fromTo.toBase;
  }

  const toFrom = registryGetConversion(to, from);
  if (toFrom) {
    return toFrom.fromBase;
  }

  const baseUnits = common ? [common] : findBaseUnits(from, to);

  if (baseUnits.length === 0) {
    try {
      return makeCompoundConversion(expandUnit(from), expandUnit(to));
    } catch (e) {
      if (e instanceof NoConversionError) {
        throw new NoConversionError(from, to);
      }
      throw e;
    }
  }

  MultipleBaseUnitsError.invariant(baseUnits.length === 1, baseUnits);

  const toBase = registryGetConversion(from, baseUnits[0])?.toBase;
  const fromBase = registryGetConversion(to, baseUnits[0])?.fromBase;

  NoConversionError.invariant(!!(toBase && fromBase), from, to);

  return (v: ASTExpr) => fromBase(toBase(v));
}

// This basically replaces all the units in the expression with the base unit
// of the same type, and keeps track of the conversions necessary along the way
function reduceCompoundUnitToBaseUnits(unit: Unit): {
  newUnit: Unit;
  toBaseConversion: (v: ASTExpr) => ASTExpr;
  fromBaseConversion: (v: ASTExpr) => ASTExpr;
} {
  // Base unit
  let newUnit = NullUnit;
  // Base identity converter
  let toBaseConversion = (v: ASTExpr) => v;
  let fromBaseConversion = (v: ASTExpr) => v;

  for (const [key, power] of unit) {
    const atomicUnit = getUnitBang(key);

    if (isBaseUnit(atomicUnit)) {
      newUnit = multiplyUnits(newUnit, exponentiateUnit(atomicUnit, power));
      continue;
    }

    const baseUnits = baseUnitsFor(atomicUnit);

    MultipleBaseUnitsError.invariant(baseUnits.length === 1, baseUnits);

    const baseUnit = baseUnits[0];

    newUnit = multiplyUnits(newUnit, exponentiateUnit(baseUnit, power));

    const conversion = must(registryGetConversion(atomicUnit, baseUnit));
    let toBase: (v: ASTExpr) => ASTExpr;
    let fromBase: (v: ASTExpr) => ASTExpr;

    if (power > 0) {
      toBase = conversion.toBase;
      fromBase = conversion.fromBase;
    } else {
      fromBase = conversion.toBase;
      toBase = conversion.fromBase;
    }

    // So closures keep the right functions
    times(Math.abs(power), () => {
      const oldToBaseConversion = toBaseConversion;
      const oldFromBaseConversion = fromBaseConversion;

      toBaseConversion = (v: ASTExpr) => toBase(oldToBaseConversion(v));
      fromBaseConversion = (v: ASTExpr) => oldFromBaseConversion(fromBase(v));
    });
  }

  return { newUnit, toBaseConversion, fromBaseConversion };
}

// Attempt to find a converter from one unit to another by matching up atomic units
// that are commensurable.  If no converter can be found, throw an error.
//
// This is O(n^2), which is not great, but n should be small.
//
function makeCompoundConversion(from: Unit, to: Unit): (v: ASTExpr) => ASTExpr {
  const { newUnit: fromUnit, toBaseConversion: fromConversion } =
    reduceCompoundUnitToBaseUnits(from);

  const { newUnit: toUnit, fromBaseConversion: toConversion } =
    reduceCompoundUnitToBaseUnits(to);

  NoConversionError.invariant(fromUnit.size === toUnit.size, fromUnit, toUnit);

  // If the reduced units are the same, we already have the conversions
  if (isUnitEqual(fromUnit, toUnit)) {
    return (v: ASTExpr) => toConversion(fromConversion(v));
  }

  const baseUnits = findBaseUnits(fromUnit, toUnit);

  // If there's exactly one base unit between the reduced units, we can just use it for conversion
  if (baseUnits.length === 1) {
    const converter = findUnitConverterAst(fromUnit, toUnit, baseUnits[0]);
    return (v: ASTExpr) => toConversion(converter(fromConversion(v)));
  }

  MultipleBaseUnitsError.invariant(baseUnits.length === 0, baseUnits);

  // Base identity converter
  let converter = (v: ASTExpr) => v;

  for (const [fromKey, fromPower] of from) {
    const fromUnit = getUnitBang(fromKey);
    let targetUnit = undefined;
    let baseUnits: Array<Unit> | undefined = undefined;

    for (const [toKey, toPower] of to) {
      const toUnit = getUnitBang(toKey);

      if (fromPower !== toPower) continue;

      const pairBaseUnits = findBaseUnits(fromUnit, toUnit);
      if (pairBaseUnits.length === 0) {
        continue;
      }

      AmbiguousConversionError.invariant(!targetUnit, from, to);

      targetUnit = toUnit;
      baseUnits = pairBaseUnits;
    }

    if (!targetUnit || !baseUnits || baseUnits.length === 0) {
      throw new NoConversionError(from, to);
    }

    if (fromPower > 0) {
      const subConverter = findUnitConverterAst(
        fromUnit,
        targetUnit,
        baseUnits[0]
      );

      times(fromPower, () => {
        const oldConverter = converter;
        converter = (v: ASTExpr) => subConverter(oldConverter(v));
      });
    } else {
      const subConverter = findUnitConverterAst(
        targetUnit,
        fromUnit,
        baseUnits[0]
      );

      times(-fromPower, (n) => {
        const oldConverter = converter;
        converter = (v: ASTExpr) => subConverter(oldConverter(v));
      });
    }
  }

  return converter;
}

/*
 * Returns a function that converts a scalar number from one unit to another
 */
export function findUnitConverter(
  from: Unit,
  to: Unit,
  common?: Unit
): (v: number) => number {
  const astConversion = findUnitConverterAst(from, to, common);
  const ctx: Context = emptyContext();
  return (v: number) => {
    const ast = astConversion({ t: 'number', v });
    const rval = evalLimitedExpression(ctx, ast);
    invariant(typeof rval === 'number', 'Conversion did not return a number');
    return rval;
  };
}

/*
 * Convert a scalar number from one specified unit to another.
 */
export function convertUnit(quantity: number, from: Unit, to: Unit): number {
  return findUnitConverter(from, to)(quantity);
}
